import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, RawAxiosRequestHeaders } from 'axios';
import { stringifyQuery } from '@jjj/components';
import { IRequestParameters, IResponse } from '@dash/apiInstance';
import { getOrganizationCode } from 'src/settings/organizationSettings';
import { isArray } from 'lodash';
import settings from '../settings';
import { getAuthData, clearAuthData } from './auth';
import { LOCAL_SETTINGS_KEY } from './localStorageKeys.const';
import { store } from '../redux/store';
import { signInRoute } from '../router';

export const defaultApiVersionPath = '/api/v1';
const REFRESH_PATH = 'refresh';
const broadcast = new BroadcastChannel('session');

// Rest API url
const getApiUrl = (versionPath: string = defaultApiVersionPath, type: 'base' | 'UnifiedLogin' = 'base') => {
  const localStorageSettings = localStorage.getItem(LOCAL_SETTINGS_KEY);
  const appSettings = localStorageSettings ? JSON.parse(localStorageSettings) : settings;
  const url = new URL(`${type === 'UnifiedLogin' ? settings.loginApiUrl : appSettings.apiUrl}${versionPath}`);

  return url.href;
};

function getContentTypeByFileType(fileType?: string): string {
  switch (fileType) {
    case 'csv':
      return 'text/csv';
    case 'pdf':
      return 'application/pdf';
    default:
      return 'application/json';
  }
}

export function getRequestHeaders(
  contentType?: string,
  optionalAcceptType?: string,
  optionalHeader?: { [key: string]: string },
  type: 'base' | 'UnifiedLogin' = 'base',
): RawAxiosRequestHeaders {
  const contentTypeHeader = getContentTypeByFileType(contentType);
  const acceptType = optionalAcceptType || null;
  const auth = getAuthData();
  const isUnifiedLoginEnabled = store?.getState()?.FeatureFlags?.flags?.unified_login_enabled;

  const headers: RawAxiosRequestHeaders = {
    Accept: acceptType || contentTypeHeader,
    'Content-Type': contentTypeHeader,
    ...(!isUnifiedLoginEnabled && type === 'base' && { 'ORGANIZATION-CODE': getOrganizationCode() as string }),
    'OS-Type': 'web',
    ...(optionalHeader && { ...optionalHeader }),
    ...(auth && !(auth instanceof Map) && 'authToken' in auth && 'id' in auth && auth?.authToken
      ? { Authorization: auth.authToken, ...(!isUnifiedLoginEnabled && { id: auth.id }) }
      : {}),
  };

  return headers;
}

const axiosInstance = axios.create({
  // @ts-ignore
  mode: 'cors',
  withCredentials: true,
});

let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;
let isLoggingOut = false;

const logout = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  const state = store.getState();
  const isUnifiedLoginEnabled = state?.FeatureFlags?.flags?.unified_login_enabled;

  broadcast.postMessage({ type: 'logout' });

  if (!isUnifiedLoginEnabled) {
    clearAuthData();
    sessionStorage.clear();

    window.location.href = signInRoute;

    return;
  }

  window.location.href = settings.loginUrl as string;
};

const callRefresh = async (): Promise<void> => {
  const { default: ApiInstance } = await import('./apiInstance');
  const refreshApi = new ApiInstance(REFRESH_PATH, '/v1', 'UnifiedLogin');

  const doRefresh = () =>
    refreshApi.post({
      data: {},
      contentTypeHeader: 'application/json',
      acceptHeader: 'application/json',
    });

  await doRefresh();
};

const refreshToken = async (): Promise<void> => {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = callRefresh()
      .catch((refreshError: unknown) => {
        throw refreshError;
      })
      .finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
  }

  await refreshPromise!;
};

interface Handle401Options {
  error: AxiosError;
  isRefreshRequest?: boolean;
  retryFn: () => Promise<any>;
}

export const handle401Error = async <T>(options: Handle401Options): Promise<T> => {
  const { isRefreshRequest = false, retryFn, error } = options;
  const state = store.getState();
  const isUnifiedLoginEnabled = state?.FeatureFlags?.flags?.unified_login_enabled;

  if (!isUnifiedLoginEnabled) {
    return Promise.reject(error);
  }

  if (isRefreshRequest) {
    logout();

    throw new Error('Refresh token expired');
  }

  try {
    await refreshToken();

    return await retryFn();
  } catch (refreshError) {
    logout();

    throw refreshError;
  }
};

axiosInstance.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const { response } = error;
    const originalRequest = error.config as AxiosRequestConfig & { isRetry?: boolean };

    if (!response) {
      return Promise.reject(error);
    }

    if (response.status !== 401) {
      return Promise.reject(error);
    }

    if (originalRequest.isRetry) {
      logout();

      return Promise.reject(error);
    }

    originalRequest.isRetry = true;

    const requestUrl = typeof originalRequest.url === 'string' ? originalRequest.url : '';
    const isRefreshRequest = requestUrl.includes(REFRESH_PATH);

    return handle401Error({
      error,
      isRefreshRequest,
      retryFn: () => {
        return axiosInstance(originalRequest);
      },
    });
  },
);

broadcast.onmessage = event => {
  if (event.data?.type === 'logout') {
    logout();
  }
};

function parseJSON<T extends IResponse<T>>(json: AxiosResponse<T>) {
  const { status, data } = json;
  const responseData = data?.data || data?.success?.data || data;
  const responseMessages = data?.messages || data?.success?.messages;

  let response = {
    ...(responseData && Object.keys(responseData).length > 0 && { data: responseData }),
    ...(responseMessages && { messages: responseMessages }),
    status,
  };

  if (data) {
    const { success: _, data: __, ...rest } = data;

    response = {
      ...response,
      ...rest,
      status,
    };
  }

  return response;
}

function parseError(errorResponse: { response: AxiosResponse }) {
  const { data } = errorResponse?.response || { data: {} };
  const responseData = data?.errors?.data;

  const validations = isArray(responseData?.validations)
    ? responseData?.validations
    : responseData?.validations && Object.values(responseData?.validations);

  const baseValidationsErrors = validations?.reduce(
    (accumulator: [string], currentValue: { field: string; messages: [string] }) => {
      if (currentValue.field === 'base' && currentValue?.messages?.length > 0) {
        return accumulator.concat(currentValue.messages);
      }

      return accumulator;
    },
    [],
  );

  return {
    status: errorResponse.response?.status,
    ...(responseData && Object.keys(responseData)?.length > 0 && { data: responseData }),
    ...(baseValidationsErrors?.length > 0 && { validationMessages: baseValidationsErrors }),
    ...(data?.errors?.messages && { messages: data.errors.messages }),
    ...(data?.error && { error: data.error }),
  };
}

function getHeaderStringValue(headers: AxiosResponse['headers'], headerName: string): string | null {
  const axiosHeadersValue =
    headers && 'get' in headers && typeof headers.get === 'function' ? headers.get(headerName) : null;
  const headerValue = axiosHeadersValue ?? headers?.[headerName];

  if (typeof headerValue === 'string') {
    return headerValue;
  }

  if (Array.isArray(headerValue)) {
    return headerValue[0] || null;
  }

  if (typeof headerValue === 'number' || typeof headerValue === 'boolean') {
    return String(headerValue);
  }

  return null;
}

function parseFile(response: AxiosResponse) {
  let fileName = null;
  const contentDisposition = getHeaderStringValue(response.headers, 'content-disposition');

  if (contentDisposition && contentDisposition.split('filename="')[1]) {
    [fileName] = contentDisposition.split('filename="')[1].split('"');
  }

  const blob = new Blob([response.data]);
  const fileUrl = URL.createObjectURL(blob);
  response.data = { fileBlobUrl: fileUrl, fileName };

  return response;
}

function parseResponse<T extends IResponse<T>>(response: AxiosResponse<T>) {
  const contentTypeValue = getHeaderStringValue(response.headers, 'content-type');
  const contentType = contentTypeValue ? contentTypeValue.split(';')[0] : null;

  switch (contentType) {
    case 'application/json':
      return parseJSON<T>(response);
    case 'application/pdf':
    case 'text/csv':
    case 'image/jpeg':
    case 'image/png':
    case 'image/jpg':
    case 'image/gif':
    case 'application/octet-stream':
      return parseFile(response);
    default:
      return response;
  }
}

export function apiRequest<T extends IResponse<T>>(options: AxiosRequestConfig) {
  return axiosInstance
    .request<T>({ ...options })
    .then(response => parseResponse<T>(response))
    .catch(error => parseError(error));
}

function getPath(pathTemplate: string, params?: IRequestParameters): string {
  let url = pathTemplate;

  if (params && Object.keys(params).length > 0) {
    Object.keys(params).forEach(key => {
      if (Object.prototype.hasOwnProperty.call(params, key) && params[key]) {
        url = url.replace(`:${key}`, params[key]);
      }
    });
  }

  return url;
}

export function getRequestUrl(
  search: string,
  pathTemplate: string,
  versionPath: string,
  pathParams?: IRequestParameters,
  type: 'base' | 'UnifiedLogin' = 'base',
) {
  const query: string = stringifyQuery(search);
  const path = getPath(pathTemplate, pathParams);

  // actions contains already API version path
  const formattedVersionPath = path.startsWith(versionPath) ? '' : versionPath;

  return `${getApiUrl(formattedVersionPath, type)}/${path}${query ? `?${query}` : ''}`;
}
